import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { invalidateTaskQueries } from "@/lib/task-query-utils";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Paperclip,
  Download,
  Trash2,
  Loader2,
  PawPrint,
  Plus,
} from "lucide-react";
import { FileViewerDialog, getFileTypeIcon, isPreviewable } from "@/components/ui/file-viewer-dialog";
import type { ViewerFile } from "@/components/ui/file-viewer-dialog";
import { FileUploadDialog } from "@/components/ui/file-upload-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface AttachmentAnimal {
  id: string;
  fileId: string;
  animalId: string;
  animalName: string | null;
}

interface Attachment {
  id: string;
  entityType: string;
  entityId: string;
  filename: string;
  displayName: string | null;
  description: string | null;
  fileSize: number;
  mimeType: string;
  s3Key: string;
  s3Bucket: string;
  createdBy: string | null;
  createdAt: string;
  createdByUsername: string | null;
  createdByFirstName: string | null;
  createdByLastName: string | null;
  animals: AttachmentAnimal[];
}

interface AnimalSuggestion {
  animalId: string;
  animalName: string | null;
  animalSpecies: string | null;
  suggested: boolean;
  linkedToTask: boolean;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatUploaderName(attachment: Attachment): string {
  if (attachment.createdByFirstName && attachment.createdByLastName) {
    return `${attachment.createdByFirstName} ${attachment.createdByLastName}`;
  }
  return attachment.createdByUsername ?? "Unknown";
}

interface TaskAttachmentsSectionProps {
  taskId: string;
  canEdit: boolean;
}

export function TaskAttachmentsSection({ taskId, canEdit }: TaskAttachmentsSectionProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedAnimalIds, setSelectedAnimalIds] = useState<Set<string>>(new Set());
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);

  const { data, isLoading } = useQuery<{ files: Attachment[] }>({
    queryKey: ["/api/tasks", taskId, "attachments"],
    queryFn: () => apiRequest("GET", `/api/tasks/${taskId}/attachments`),
  });

  const { data: suggestionsData } = useQuery<{ animals: AnimalSuggestion[] }>({
    queryKey: ["/api/tasks", taskId, "attachments", "animal-suggestions"],
    queryFn: () => apiRequest("GET", `/api/tasks/${taskId}/attachments/animal-suggestions`),
    staleTime: 30_000,
  });

  const uploadMutation = useMutation({
    mutationFn: async ({ file, displayName, description }: { file: File; displayName: string; description: string }) => {
      const formData = new FormData();
      formData.append("file", file);
      if (displayName) formData.append("displayName", displayName);
      if (description) formData.append("description", description);
      const animalIds = Array.from(selectedAnimalIds);
      if (animalIds.length > 0) {
        formData.append("animalIds", JSON.stringify(animalIds));
      }
      return apiRequest("POST", `/api/tasks/${taskId}/attachments`, formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/tasks", taskId, "attachments"] });
      invalidateTaskQueries();
      setUploadDialogOpen(false);
      setSelectedAnimalIds(new Set());
      toast({ title: "File uploaded" });
    },
    onError: (err: Error) => {
      toast({
        variant: "destructive",
        title: "Upload failed",
        description: err.message,
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (fileId: string) => {
      return apiRequest("DELETE", `/api/tasks/${taskId}/attachments/${fileId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/tasks", taskId, "attachments"] });
      invalidateTaskQueries();
      toast({ title: "Attachment removed" });
    },
    onError: (err: Error) => {
      toast({
        variant: "destructive",
        title: "Delete failed",
        description: err.message,
      });
    },
  });

  const [viewerFiles, setViewerFiles] = useState<ViewerFile[]>([]);

  const handleOpenViewer = async (index: number) => {
    const previewableAtts = attachments.filter((a) => isPreviewable(a.mimeType));
    if (previewableAtts.length === 0) return;

    const att = attachments[index];
    if (!isPreviewable(att.mimeType)) {
      window.open(`/api/tasks/${taskId}/attachments/${att.id}/download`, "_blank");
      return;
    }

    try {
      const resolved = await Promise.all(
        previewableAtts.map(async (a) => {
          const res = await apiRequest("GET", `/api/files/${a.id}/url`);
          return {
            url: (res as { url: string }).url,
            mimeType: a.mimeType,
            filename: a.displayName || a.filename,
            description: a.description,
            animals: a.animals?.map(an => ({ animalId: an.animalId, animalName: an.animalName })),
          };
        })
      );
      const previewableIndex = previewableAtts.indexOf(att);
      setViewerFiles(resolved);
      setViewerIndex(previewableIndex >= 0 ? previewableIndex : 0);
      setViewerOpen(true);
    } catch {
      toast({ variant: "destructive", title: "Preview failed", description: "Downloading file instead." });
      window.open(`/api/tasks/${taskId}/attachments/${att.id}/download`, "_blank");
    }
  };

  const toggleAnimalSelection = (animalId: string) => {
    setSelectedAnimalIds(prev => {
      const next = new Set(prev);
      if (next.has(animalId)) {
        next.delete(animalId);
      } else {
        next.add(animalId);
      }
      return next;
    });
  };

  const attachments = data?.files ?? [];
  const animals = suggestionsData?.animals ?? [];

  return (
    <div data-testid="task-attachments-section">
      <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1 mb-2">
        <Paperclip className="w-3 h-3" /> Attachments
        {attachments.length > 0 && (
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 ml-1" data-testid="badge-attachment-count">
            {attachments.length}
          </Badge>
        )}
        {canEdit && (
          <Button
            variant="ghost"
            size="icon"
            className="ml-auto h-6 w-6"
            onClick={() => { setSelectedAnimalIds(new Set()); setUploadDialogOpen(true); }}
            data-testid="button-add-attachment"
          >
            <Plus className="w-3.5 h-3.5" />
          </Button>
        )}
      </Label>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-2" data-testid="text-loading-attachments">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading attachments...
        </div>
      ) : attachments.length === 0 ? (
        <p className="text-sm text-muted-foreground py-1" data-testid="text-no-attachments">
          No attachments yet.
        </p>
      ) : (
        <div className="space-y-1" data-testid="list-attachments">
          {attachments.map((att, attIndex) => {
            const IconComponent = getFileTypeIcon(att.mimeType);
            return (
              <div
                key={att.id}
                className="flex items-center justify-between gap-2 py-1.5 group"
                data-testid={`attachment-item-${att.id}`}
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <IconComponent className="w-4 h-4 text-muted-foreground shrink-0" />
                  <div className="min-w-0 flex-1">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={() => handleOpenViewer(attIndex)}
                          className="text-sm truncate block hover:underline text-left cursor-pointer"
                          data-testid={`link-attachment-${att.id}`}
                        >
                          {att.displayName || att.filename}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        <p>{att.displayName || att.filename}</p>
                        {att.description && <p className="text-xs text-muted-foreground">{att.description}</p>}
                        <p className="text-xs text-muted-foreground">
                          {formatFileSize(att.fileSize)} &middot; {formatUploaderName(att)} &middot;{" "}
                          {new Date(att.createdAt).toLocaleDateString()}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                    <span className="text-[11px] text-muted-foreground">
                      {formatFileSize(att.fileSize)} &middot; {new Date(att.createdAt).toLocaleDateString()}
                      {att.animals.length > 0 && (
                        <>
                          {" "}&middot;{" "}
                          {att.animals.map((a) => a.animalName ?? a.animalId).join(", ")}
                        </>
                      )}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-0.5 invisible group-hover:visible">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <a
                        href={`/api/tasks/${taskId}/attachments/${att.id}/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`button-download-attachment-${att.id}`}
                      >
                        <Button variant="ghost" size="icon">
                          <Download className="w-3.5 h-3.5" />
                        </Button>
                      </a>
                    </TooltipTrigger>
                    <TooltipContent>Download</TooltipContent>
                  </Tooltip>
                  {canEdit && (att.createdBy === user?.id || user?.isAdmin) && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteMutation.mutate(att.id)}
                          disabled={deleteMutation.isPending}
                          data-testid={`button-delete-attachment-${att.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Remove</TooltipContent>
                    </Tooltip>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <FileUploadDialog
        open={uploadDialogOpen}
        onOpenChange={(open) => { if (!open) { setUploadDialogOpen(false); setSelectedAnimalIds(new Set()); } else { setUploadDialogOpen(true); } }}
        onUpload={(file, displayName, description) => uploadMutation.mutate({ file, displayName, description })}
        isPending={uploadMutation.isPending}
        title="Upload Attachment"
        dialogDescription="Upload a file to this task."
      >
        {animals.length > 0 && (
          <div>
            <Label>Tag Animals (optional)</Label>
            <p className="text-xs text-muted-foreground mb-1">
              Link this file to one or more animals so it appears on their BETI profile.
            </p>
            <div className="space-y-2" data-testid="list-tag-animals">
              {animals.map((animal) => (
                <div key={animal.animalId} className="flex items-center gap-2">
                  <Checkbox
                    id={`tag-animal-${animal.animalId}`}
                    checked={selectedAnimalIds.has(animal.animalId)}
                    onCheckedChange={() => toggleAnimalSelection(animal.animalId)}
                    data-testid={`checkbox-tag-animal-${animal.animalId}`}
                  />
                  <Label
                    htmlFor={`tag-animal-${animal.animalId}`}
                    className="text-sm font-normal cursor-pointer flex items-center gap-1.5"
                    data-testid={`label-tag-animal-${animal.animalId}`}
                  >
                    <PawPrint className="w-3.5 h-3.5 text-muted-foreground" />
                    {animal.animalName ?? animal.animalId}
                    {animal.animalSpecies && (
                      <span className="text-xs text-muted-foreground">({animal.animalSpecies})</span>
                    )}
                    {animal.suggested && (
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0" data-testid={`badge-suggested-${animal.animalId}`}>
                        Suggested
                      </Badge>
                    )}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        )}
      </FileUploadDialog>

      <FileViewerDialog
        open={viewerOpen}
        onOpenChange={setViewerOpen}
        files={viewerFiles}
        initialIndex={viewerIndex}
      />
    </div>
  );
}
